/**
 * Deliverable 2 — Level Data Format (THE shared spine).
 *
 * This is the contract the future editor WRITES, the engine READS, and the
 * server STORES. Lock it carefully; bump `version` for breaking changes.
 *
 * ── JSON shape (documented) ─────────────────────────────────────────────────
 * {
 *   "version": 1,                       // schema version int — bump-able
 *   "meta": {
 *     "name":        "Test 01",
 *     "author":      "engine-step-1",
 *     "createdAt":   "2026-06-29T00:00:00.000Z",   // ISO-8601
 *     "formProfile": "smb1"             // which physics preset SET it was authored against
 *   },
 *   "dimensions": { "widthTiles": 64, "heightTiles": 16, "tileSize": 64 },
 *
 *   // tiles: ROW-MAJOR dense 2D grid. tiles[row][col].
 *   //   row 0 = TOP of the level, col 0 = LEFT. Length must equal
 *   //   heightTiles rows × widthTiles cols. Values are TileType ids.
 *   //   (Dense grid chosen over RLE/sparse for Step 1: trivial to author by
 *   //    hand and to index in collision. Format can gain an RLE codec later
 *   //    behind the same `version` gate without changing the engine read path.)
 *   "tiles": [[0,0,0,...],[...],...],
 *
 *   // entities: gameplay objects placed in TILE coordinates (x = col, y = row),
 *   //   measured from the entity's tile cell. Engine converts to px via tileSize.
 *   "entities": [
 *     { "type": "PLAYER_START", "x": 2,  "y": 12 },
 *     { "type": "GOOMBA",       "x": 20, "y": 12 },
 *     { "type": "GOAL_FLAG",    "x": 61, "y": 11 }
 *   ],
 *
 *   "theme": "overworld"               // selects the AssetManifest to load
 * }
 * ────────────────────────────────────────────────────────────────────────────
 */

/** Tile type ids stored in the dense grid. Keep ints stable across versions. */
export enum TileType {
  EMPTY = 0,
  /** Full collision on all sides. */
  SOLID = 1,
  /** Jump-through platform: collides ONLY when the player is moving downward and
   *  their feet were above the tile's top edge last frame (one-way from below). */
  SEMISOLID = 2,
  /** Touching a GOAL tile triggers Level Clear. (Goal can also be an entity flag.) */
  GOAL = 3,
}

/**
 * Placeable entity kinds. PLAYER_START is required; the rest are optional.
 *
 * ── ROSTER FREEZE (Brief 004 / decision R2) ─────────────────────────────────
 * The level editor's palette is built off THIS enum (+ the editor's roster table).
 * Isaac locked the full type list before the palette shipped so it never churns:
 * SPIKE / COIN / FLYER were added with BEHAVIOUR STUBBED — they are placeable and
 * serialize round-trip, but the engine does NOT yet simulate them (no collision /
 * pickup / AI). A later brief wires the behaviour without touching the format.
 * Additive string enum values → no `version` bump; `validateLevel` is unchanged.
 */
export enum EntityType {
  PLAYER_START = 'PLAYER_START',
  GOAL_FLAG = 'GOAL_FLAG',
  /** Placeholder enemy (rendered as a Kenney slime). Minimal walker for Step 1. */
  GOOMBA = 'GOOMBA',
  /** Hazard (spikes/saw). STUBBED: placeable + serialized, no engine behaviour yet. */
  SPIKE = 'SPIKE',
  /** Pickup (coin). STUBBED: placeable + serialized, no engine behaviour yet. */
  COIN = 'COIN',
  /** Second enemy (flying). STUBBED: placeable + serialized, no engine behaviour yet. */
  FLYER = 'FLYER',
  /**
   * Brief 008 archetype enemies — purely ADDITIVE (version stays 1; validateLevel is
   * type-agnostic so no validator change). Each is one EntityType + a `props.variant`
   * selecting sprite + tuning, mirroring the FLYER pattern. GOOMBA is untouched (it
   * stays the reference walker); WALKER is the new generic ground patroller.
   */
  /** Generic ground patroller — `props.variant` (snail/mouse/spiny/…) selects sprite +
   *  tuning (speed, spiked, turnAtLedge). Spiked variants are un-stompable. */
  WALKER = 'WALKER',
  /** Periodic deterministic hopper (frog). Charges a jump on a fixed-step interval. */
  HOPPER = 'HOPPER',
  /** Anchored ambusher (spider) — drops toward the player when in range, returns to
   *  anchor. Un-stompable (hurts on any contact). */
  AMBUSH = 'AMBUSH',
  /**
   * Brief 009 interactive blocks — ADDITIVE (version stays 1; validateLevel is
   * type-agnostic). These are SOLID entities that occupy ONE tile cell and act as
   * colliders (NOT tiles — they serialize in entities[], tile-aligned). Each reacts
   * to a head-bonk / top-landing via the Brief 009 spine extension.
   */
  /** `?`-block: head-bonk pops `props.reward` (COIN-only in 009; 010 widens to items)
   *  then goes inert. Author an already-spent one with `props.spent: true`. */
  QBLOCK = 'QBLOCK',
  /** Brick: head-bonk pops `props.reward` (like a `?`-block) if it hides one, else it
   *  bumps. (`props.breakable` is accepted-but-inert since the SMALL/BIG form toggle was
   *  removed — a destructible wall is a CRACKEDWALL.) */
  BRICK = 'BRICK',
  /** Spring: top-landing launches the player upward at `props.power` (px/s). Solid;
   *  not bonkable. */
  SPRING = 'SPRING',
  /**
   * Brief 010 power-up pickup — ADDITIVE (version stays 1; validateLevel is
   * type-agnostic). ONE type, `props.kind` selects the effect (mirrors FLYER/WALKER
   * variant): 'heart' (refill +amount up to maxHearts), 'heartContainer' (raise
   * maxHearts +1, capped), 'star' (timed invuln for props.durationMs). NOT a hidden
   * health layer — it touches the hearts meter or a timed buff only (Isaac-locked
   * Zelda-hearts model). An overlap-collect entity like COIN; a `?`-block can emit one
   * via the reward token ITEM_HEART / ITEM_HEART_CONTAINER / ITEM_STAR.
   */
  ITEM = 'ITEM',
  /**
   * Brief 011 terrain mechanisms — ADDITIVE (version stays 1; validateLevel is
   * type-agnostic). Two families:
   *
   *  KINEMATIC SOLIDS (reuse the Brief 009 solid spine, add MOTION):
   *   - PLATFORM: a solid that follows a deterministic ping-pong path
   *     (`props.axis` 'x'|'y', `props.range` in tiles, `props.speed` px/s); the player
   *     standing on its top is carried with it. `props.w`/`props.h` (tiles) size the bar.
   *   - CONVEYOR: a (usually static) solid belt — `props.surfaceSpeed` (px/s, signed)
   *     is added to a grounded rider's horizontal motion each step.
   *
   *  CONNECTIVITY (paired teleporters — legally-distinct portals, NOT pipes):
   *   - WARP: overlap its mouth → teleport to the WARP/DOOR sharing `props.linkId`.
   *   - DOOR: same, gated on the `up`/enter press (press E in the doorway).
   *  Pairing is by `props.linkId` (int); place exactly two with the same id.
   */
  PLATFORM = 'PLATFORM',
  CONVEYOR = 'CONVEYOR',
  WARP = 'WARP',
  DOOR = 'DOOR',
  /**
   * Brief 012 hazards — ADDITIVE (version stays 1; validateLevel is type-agnostic).
   * Widen the hazard set beyond the single static SPIKE. All are ENTITIES (the
   * "no new TileType" lock makes lava an entity, not a tile):
   *
   *  - LAVA: a tile-aligned hazard ZONE spanning `props.wCells × hCells` cells —
   *    "SPIKE over a region". Always-hurt; `props.instakill` (default true) respawns
   *    on touch (the fall-out-of-world path) instead of costing one heart.
   *  - SAW: always-hurt like SPIKE; the spin is a visual frame-swap only (no collision
   *    rotation). `props.path` rides a deterministic ping-pong track (`axis`/`range`/
   *    `speed`, reusing the Brief 011 kinematic-path model).
   *  - FIREBAR: `props.segments` fire orbs at fixed radii from a pivot, all sharing one
   *    integer angle index (advanced per fixed step, `props.speed` = steps/revolution).
   *    Positions come from the precomputed circle LUT (engine/circleLut.ts) — NO trig,
   *    so it survives the determinism lint. Each orb is an always-hurt child entity.
   *  - THWOMP: a SOLID (Brief 009) that rests up high and drops fast when the player
   *    enters its column below within `props.triggerRange`, pauses, then returns. Contact
   *    while falling = hurt (or instakill if `props.crush`); idle = a standable solid.
   */
  LAVA = 'LAVA',
  SAW = 'SAW',
  FIREBAR = 'FIREBAR',
  THWOMP = 'THWOMP',
  /**
   * Brief 013 checkpoint — ADDITIVE (version stays 1; validateLevel is type-agnostic).
   * An overlap entity (NOT solid, NOT a hazard) that, on first touch, OVERRIDES the
   * level's respawn point to its own position (hearts-reset-on-death semantics are
   * unchanged). Idempotent (re-touch = no-op). Multiple may exist; the most-recently
   * touched wins. Flips to an "activated" sprite once touched.
   */
  CHECKPOINT = 'CHECKPOINT',
  /**
   * Brief 014 variant-object kit — ADDITIVE (version stays 1; validateLevel is
   * type-agnostic). The load-bearing per-archetype OBJECT gaps surfaced by the object
   * research pass (docs/research/objects/), built ONCE and shared across the reference
   * games that need them. All reuse the Brief 009/011 solid + overlap spine; no new
   * input bit (they react to position / collision / a global latch), so a replay drives
   * them identically.
   */
  /** CRUMBLE block (SMB / Celeste / Super Meat Boy): a SOLID that, once stood on, runs an
   *  integer fuse then goes non-solid + invisible, and (optionally) respawns after a delay.
   *  `props.fuseSteps` / `props.respawnSteps` (0 = never returns). */
  CRUMBLE = 'CRUMBLE',
  /** LAUNCHPAD (Sonic dash-pad / N++ launch pad): a SOLID pad that fires the player as a
   *  DIRECTIONAL impulse on top-landing — `props.dir` (8-way) + `props.power` (px/s). The
   *  angled cousin of SPRING (vertical-only). */
  LAUNCHPAD = 'LAUNCHPAD',
  /** BUMPER (Sonic bumper / Celeste bumper): a non-solid overlap object that knocks the
   *  player RADIALLY away from its center at `props.power` (px/s). */
  BUMPER = 'BUMPER',
  /** DREAM BLOCK (Celeste): a block that is SOLID normally but PASS-THROUGH while the player
   *  is dashing — the dash carries straight through. `props.w`/`props.h` (tiles) size it. */
  DREAMBLOCK = 'DREAMBLOCK',
  /** EXIT SWITCH (N++ / Mario P-switch / Hollow-Knight lever): touching it OPENS the level's
   *  exit gate(s) via a World-level boolean latch (reset on respawn → replay-safe). */
  EXITSWITCH = 'EXITSWITCH',
  /** EXIT GATE (N++ locked exit door): a SOLID barrier while the exit switch is UNHIT; once
   *  any EXITSWITCH is touched it goes non-solid + opens. The switch→gate pair is the one
   *  global-latch mechanism. */
  EXITGATE = 'EXITGATE',
  /** FAN / air-current (Super Meat Boy): a tile-aligned ZONE (`props.wCells × hCells`) that
   *  PUSHES the player a `props.dir` (4-way) at `props.power` (px/s) while overlapping. */
  FAN = 'FAN',
  /** LASER (Super Meat Boy): a timed on/off BEAM hazard spanning `props.length` cells along
   *  `props.axis`; lethal only while ON (integer `props.onSteps`/`offSteps` cycle). */
  LASER = 'LASER',
  /**
   * Brief 014 P1 follow-ups — ADDITIVE (version stays 1). Three more objects that fill the
   * P1 gaps the object research backlog named; like the P0 set, all reuse the existing
   * solid/overlap/attack spine.
   */
  /** MINE (N++): a small round ONE-TOUCH instakill dot — a SPIKE that kills outright. */
  MINE = 'MINE',
  /** CRACKED WALL (Hollow Knight): a SOLID broken by the player's ATTACK hitbox. May hide a
   *  `props.reward`. (The BRICK is never destructible — see its note.) */
  CRACKEDWALL = 'CRACKEDWALL',
  /** MOVE BLOCK / Kevin (Celeste): a SOLID that LAUNCHES along the dash direction when the
   *  player dashes into it, travels until it hits a wall (or `props.travel` tiles), then stops. */
  MOVEBLOCK = 'MOVEBLOCK',
  /**
   * RUN-AND-GUN enemy roster (step 3) — the first RANGED enemy + the first MULTI-HIT enemy.
   * ADDITIVE (version stays 1; validateLevel is type-agnostic). Both reuse the Walker ground
   * spine (gravity + patrol/ledge). GUNNER: periodically fires an enemy PROJECTILE toward the
   * player after a short telegraph; stompable + shootable like a GOOMBA. ARMORED: a heavy
   * UN-stompable trooper with `props.hp` (default 3) that only a bullet kills — the shared kill
   * seam (Sim.killEnemiesInBox) chips its hp one hit at a time. Contact hurts (never stomps).
   */
  GUNNER = 'GUNNER',
  ARMORED = 'ARMORED',
  /**
   * RUN-AND-GUN step-4 emplacements — ADDITIVE (version stays 1). TURRET: a STATIC aim-tracking
   * gun that resolves an 8-way aim toward the player and fires an enemy PROJECTILE along it after
   * a telegraph; un-stompable, bullet-only kill, soaks `props.hp` (default 4). SPAWNER: a STATIC
   * dispenser that emits `props.spawnType` (default GOOMBA) grunts on a cadence up to
   * `props.maxAlive` (default 3) live at once — the "kill-the-source" objective; un-stompable,
   * bullet-only kill, `props.hp` (default 4). Both reuse the hp seam + runtime spawn queue.
   */
  TURRET = 'TURRET',
  SPAWNER = 'SPAWNER',
  /**
   * RUN-AND-GUN step-4 last-2 — ADDITIVE (version stays 1). STRAFER: an aerial gunship that
   * cruises at altitude (no gravity, ping-pong patrol) and fires an 8-way enemy PROJECTILE at the
   * player; stompable+shootable like a FLYER. LOBBER: a stationary thrower that lobs a GRAVITY-
   * arced enemy PROJECTILE (a grenade) ballistically at the player's x — indirect fire that clears
   * cover + punishes camping; stompable+shootable infantry.
   */
  STRAFER = 'STRAFER',
  LOBBER = 'LOBBER',
  /**
   * RUN-AND-GUN player bullet — ADDITIVE (version stays 1; validateLevel is type-agnostic).
   * The one genuinely-new sim primitive of the RUN-AND-GUN archetype: a player-fired
   * projectile that travels, despawns on a solid tile / off-world / timeout, and KILLS the
   * stomp-set enemies it overlaps (the first entity-vs-entity damage source in the engine).
   * RUNTIME-SPAWNED ONLY — never placed in level.entities[] (no palette row / roster entry),
   * so it adds nothing to computeLevelId, exactly like a `?`-block's popped COIN. Its motion
   * (vx/vy) + despawn arrive via props from the Sim's onShoot handler.
   */
  PROJECTILE = 'PROJECTILE',
  /**
   * POLISH_02 #6 — render-only decoration. ADDITIVE (version stays 1; validateLevel is
   * type-agnostic). ONE type, `props.variant` selects the art (mirrors FLYER/WALKER/ITEM):
   * 'bush' | 'rock' | 'fence' | 'sign' | 'cloud' | 'mushroom' | 'grasstuft'. Spawns NOTHING
   * in the sim — {@link spawnEntityAtPx} returns null for it exactly like PLAYER_START/
   * GOAL_FLAG, so it never enters `Sim.entities`: no body, no update, no collision, no
   * interaction, invisible to enemy/coin counters and every entity-iteration pass (update/
   * solids/overlap/killEnemiesInBox/resolveAttack/compaction). World renders it directly
   * from `level.entities[]` (like the goal-flag sprite) in a layer BEHIND gameplay entities.
   * Purely cosmetic — placing/removing/moving one never perturbs a replay.
   */
  DECOR = 'DECOR',
}

/**
 * Brief 013 clear condition — the win RULE the GOAL_FLAG is gated on. Additive,
 * optional on LevelMeta (absent ⇒ `reach_goal`, the legacy unconditional clear).
 *
 *  - `reach_goal`            touching the flag clears (default / legacy behaviour).
 *  - `collect_all_coins`     clears only once every placed COIN is collected.
 *  - `defeat_all_enemies`    clears only once every KILLABLE enemy is defeated
 *                            (stompable enemies — see isKillableEnemy; a level with
 *                            zero killable enemies satisfies this trivially).
 *  - `reach_goal_with_coins` clears once `coinsCollected >= n` (a coin threshold).
 *
 * `n` is only meaningful for `reach_goal_with_coins` (the coin threshold).
 */
export interface ClearCondition {
  kind: 'reach_goal' | 'collect_all_coins' | 'defeat_all_enemies' | 'reach_goal_with_coins';
  /** Coin threshold for `reach_goal_with_coins`; ignored by the other kinds. */
  n?: number;
}

export interface LevelEntity {
  type: EntityType;
  /** Tile-space column. */
  x: number;
  /** Tile-space row. */
  y: number;
  /** Free-form per-entity props (e.g. patrol range). Schema-light by design. */
  props?: Record<string, unknown>;
}

/**
 * Speedrun medal target times in SECONDS (author-facing unit), gold < silver < bronze.
 * A clear at-or-under a threshold earns that medal (N++-style). Additive + optional →
 * version stays 1. When absent the engine derives medals from `timeLimitSec` (see
 * medals.ts `resolveMedalTimes`); levels with neither simply award no medal.
 */
export interface MedalTimes {
  gold: number;
  silver: number;
  bronze: number;
}

export interface LevelMeta {
  name: string;
  author: string;
  /** ISO-8601 timestamp string. */
  createdAt: string;
  /** Names the physics preset SET this level was tuned against (e.g. "smb1"). */
  formProfile: string;
  /**
   * Content hash of the level (see {@link computeLevelId}). Optional + additive
   * → no `version` bump. The editor (Brief 004) stamps it; replays / leaderboards
   * (future) pin to it so they bind to a level's CONTENT, not its name or date.
   * Excluded from its own hash input.
   */
  levelId?: string;
  /**
   * Author intent: may a player override the level's `theme` with their own skin?
   * Absent === allowed (skins default-on). The editor (Brief 004) authors it; the
   * skin system (Brief 003) reads it.
   */
  allowSkinOverride?: boolean;
  /**
   * Starting hearts for this level; default 3. Additive + optional → version stays
   * 1. The editor (Brief 004 M2) authors it; the engine (007) reads it as
   * `level.meta.startHearts ?? 3` for the Zelda-hearts damage model.
   */
  startHearts?: number;
  /**
   * Brief 013 win RULE gating the GOAL_FLAG. Absent ⇒ `{ kind: 'reach_goal' }` (the
   * legacy unconditional clear), so test01/02 and every pre-013 level load unchanged.
   * The editor (SettingsPanel) authors it; the engine (World) reads it at load and
   * gates checkGoal on conditionSatisfied(). Additive + optional → version stays 1.
   */
  clearCondition?: ClearCondition;
  /**
   * Brief 013 optional level time limit in SECONDS (author-facing unit). The engine
   * converts it to fixed sim steps (`Math.round(timeLimitSec * 60)`) and counts DOWN
   * by step — never wall-clock, so a replay stays deterministic. Expiry triggers the
   * run's death (respawn). Absent / ≤0 ⇒ no timer. Additive + optional → version 1.
   */
  timeLimitSec?: number;
  /**
   * Speedrun medal target times in seconds (gold < silver < bronze). Authored in the
   * editor; the result screen + HUD show the medal earned on clear. Absent ⇒ derived
   * from {@link timeLimitSec} if present, else no medals. Additive + optional → version 1.
   */
  medalTimes?: MedalTimes;
  /**
   * Tier 2 — Fork/lineage (Feature E). The content {@link levelId} of the level this
   * one was forked from, set when the editor opens a community level via "Remix"
   * (NOT plain Open — only an explicit remix stamps lineage). EXCLUDED from
   * {@link computeLevelId} because it lives in `meta` → it never perturbs the child's
   * own id, so stamping a parent pointer keeps idempotency intact and a no-op fork maps
   * back to the parent record. Additive + optional → version stays 1.
   */
  parentLevelId?: string;
  /**
   * Tier 3 (Feature H): the author's SUGGESTED rule-variant category — the "intended way
   * to play" (e.g. `["low-grav"]`). The game pre-selects it in the mutator picker when the
   * URL carries no explicit `?m=` choice. Lives in `meta` so it is EXCLUDED from
   * {@link computeLevelId} — a suggested category never perturbs the geometry id, so a
   * level keeps ONE content id across its categories. Additive + optional → version 1.
   * Values are mutator ids (see Mutators.ts); unknown ids are dropped at read time.
   */
  suggestedMutators?: string[];
  /**
   * The authored MOVEMENT ARCHETYPE this level is played under (see Archetype.ts):
   * `'classic'` (default / today's feel) or `'precision'` (twitch speed-platformer —
   * near-total air control, wall-slide/jump, no player offense). Authored-in (Isaac,
   * 2026-06-30): fixed per level, NOT a play-time toggle. Stored as a permissive string
   * so the format can forward-carry a not-yet-built id (e.g. a future `'combat'`); an
   * unknown/absent value DEGRADES to `'classic'` at resolve time. Additive + optional →
   * version stays 1; `validateLevel` is unchanged.
   *
   * Lives in `meta`, so it is EXCLUDED from {@link computeLevelId} (geometry-only). Two
   * levels with identical geometry but different archetypes therefore share a content id
   * — which is why a run/board key carries the archetype ALONGSIDE the levelId (see
   * Replay.archetype / PbStore / the runs board key), so a Precision board never collides
   * with a Classic board of the same map.
   */
  archetype?: string;
}

export interface LevelDimensions {
  widthTiles: number;
  heightTiles: number;
  /** Edge length of one tile in px. Player/entity world coords derive from this. */
  tileSize: number;
}

export interface LevelFormat {
  /** Schema version int. */
  version: number;
  meta: LevelMeta;
  dimensions: LevelDimensions;
  /** Row-major dense grid: tiles[row][col], row 0 = top, col 0 = left. */
  tiles: TileType[][];
  entities: LevelEntity[];
  /** Asset-manifest theme key (e.g. "overworld"). */
  theme: string;
}

/**
 * POLISH_03 #5 publishable-perf-bomb caps. `validateLevel` REJECTS a level exceeding these
 * (the server's only prior bound was the 512KB request body — hundreds of SPAWNERs / tens of
 * thousands of entities passed that easily). Generous enough for any legitimately-authored
 * level; bounded so Sim's per-step entity passes (update/solids/overlap/killEnemiesInBox) can't
 * be handed unbounded work by a published level.
 */
export const MAX_LEVEL_WIDTH_TILES = 2048;
export const MAX_LEVEL_HEIGHT_TILES = 512;
export const MAX_LEVEL_ENTITIES = 1024;

/** Cheap structural validator — catches authoring mistakes early at load. */
export function validateLevel(level: LevelFormat): string[] {
  const errs: string[] = [];
  const { widthTiles, heightTiles } = level.dimensions;
  if (level.tiles.length !== heightTiles) {
    errs.push(`tiles row count ${level.tiles.length} != heightTiles ${heightTiles}`);
  }
  level.tiles.forEach((row, r) => {
    if (row.length !== widthTiles) errs.push(`row ${r} width ${row.length} != widthTiles ${widthTiles}`);
  });
  if (!level.entities.some((e) => e.type === EntityType.PLAYER_START)) {
    errs.push('no PLAYER_START entity');
  }
  // POLISH_03 #5: reject a publishable perf bomb — unbounded dimensions or entity count.
  if (widthTiles > MAX_LEVEL_WIDTH_TILES) errs.push(`widthTiles ${widthTiles} exceeds max ${MAX_LEVEL_WIDTH_TILES}`);
  if (heightTiles > MAX_LEVEL_HEIGHT_TILES) errs.push(`heightTiles ${heightTiles} exceeds max ${MAX_LEVEL_HEIGHT_TILES}`);
  if (level.entities.length > MAX_LEVEL_ENTITIES) {
    errs.push(`entity count ${level.entities.length} exceeds max ${MAX_LEVEL_ENTITIES}`);
  }
  return errs;
}

// ── Content identity (levelId) ────────────────────────────────────────────────

/**
 * Deterministic JSON: recursively sorts object keys so the SAME logical content
 * always serializes to the SAME string regardless of key insertion order. Arrays
 * keep their order (tile/entity order is meaningful). Used as the levelId input.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/**
 * FNV-1a 32-bit hash → 8-char hex. Integer-only (`Math.imul`, XOR, shifts), so it
 * obeys the engine's no-transcendentals rule and is bit-identical cross-engine.
 * Chosen over an async SHA-256 (`crypto.subtle`) so the editor can stamp the id
 * synchronously while building a level. 32 bits is ample for prototype-scale
 * uniqueness; swap to a wider/crypto digest later if collision-resistance matters.
 */
function fnv1a(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Canonical content id for a level. Hashes ONLY `dimensions + tiles + entities`
 * (the gameplay content) and DELIBERATELY excludes `meta` — so `createdAt`,
 * `author`, `name`, and `levelId` itself never perturb the id. Two levels with
 * identical geometry + entities share an id even if authored at different times;
 * touching a single tile changes it. Replays/leaderboards bind to this, not noise.
 */
export function computeLevelId(level: LevelFormat): string {
  const canonical = {
    dimensions: level.dimensions,
    entities: level.entities,
    tiles: level.tiles,
  };
  return fnv1a(stableStringify(canonical));
}
